import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText as renderContentfulRichText } from "gatsby-source-contentful/rich-text"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import {
  NodeRenderer
} from "@contentful/rich-text-react-renderer"
import { Typography } from "@eventbrite/marmalade"
import { ContentfulLivePreview } from "@contentful/live-preview"

// components
import { Alert } from "../../components/Alert/Alert"
import { VimeoVideo } from "../../components/VimeoVideo/VimeoVideo"
import { UniversalLink } from "../../components/UniversalLink/UniversalLink"

// styles
import * as styles from "./GatsbyImage.module.scss"

// types
import { RenderRichTextOptions, HelpArticleAlertData, VideoContent } from "./render_rich_text"

/**
 * Enhanced hyperlink renderer that uses UniversalLink for consistent behavior
 */
const hyperlinkRenderer: NodeRenderer = (node, children) => {
  const uri = node.data?.uri as string
  
  if (!uri) {
    return <span>{children}</span>
  }

  return (
    <UniversalLink to={uri}>
      {children}
    </UniversalLink>
  )
}

/**
 * Enhanced rich text rendering options that include global link handling
 */
export const enhancedContentfulRenderOptions: RenderRichTextOptions = {
  renderNode: {
    // Handle hyperlinks with UniversalLink
    [INLINES.HYPERLINK]: hyperlinkRenderer,
    
    // Existing renderers from the original file
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const entryId = node?.data?.target?.contentful_id

      if (node?.data?.target?.__typename === "ContentfulAlert") {
        const alertData = node.data.target as HelpArticleAlertData
        return (
          <Alert
            alertData={alertData}
            {...ContentfulLivePreview.getProps({ entryId, fieldId: "alertDescription" })}
          />
        )
      }

      if (node?.data?.target?.__typename === "ContentfulVideo") {
        const target = node?.data?.target as VideoContent
        return (
          <VimeoVideo
            src={target.videoUrl}
            caption={target?.videoCaption?.videoCaption}
            {...ContentfulLivePreview.getProps({ entryId, fieldId: "videoUrl" })}
          />
        )
      }

      if (node?.data?.target?.__typename === "ContentfulImage") {
        const image = node?.data?.target?.image

        if (image) {
          const { description, mimeType, width, height, url } = image

          if (mimeType === "image/svg+xml" && url) {
            const svgProps = width && height ? { width, height } : {}
            return (
              <img
                src={url}
                alt={description || ""}
                style={{ maxWidth: "100%" }}
                {...svgProps}
                {...ContentfulLivePreview.getProps({ entryId, fieldId: "image" })}
              />
            )
          }

          if (image?.gatsbyImageData) {
            const gatsbyImage = getImage(image.gatsbyImageData)
            if (gatsbyImage) {
              return (
                <GatsbyImage
                  className={styles?.gatsbyImageWrapper}
                  image={gatsbyImage}
                  alt={image?.description}
                  {...ContentfulLivePreview.getProps({
                    entryId,
                    fieldId: "image"
                  })}
                />
              )
            }
          }
        }
      }

      return null
    },

    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entryId = node?.data?.target?.contentful_id

      if (node?.data?.target?.__typename === "ContentfulAlert") {
        const alertData = node.data.target as HelpArticleAlertData
        return (
          <Alert
            alertData={alertData}
            {...ContentfulLivePreview.getProps({ entryId, fieldId: "alertDescription" })}
          />
        )
      }

      if (node?.data?.target?.__typename === "ContentfulVideo") {
        const target = node?.data?.target as VideoContent
        return (
          <VimeoVideo
            src={target.videoUrl}
            caption={target?.videoCaption?.videoCaption}
            {...ContentfulLivePreview.getProps({ entryId, fieldId: "videoUrl" })}
          />
        )
      }

      if (node?.data?.target?.__typename === "ContentfulImage") {
        const image = node?.data?.target?.image

        if (image) {
          const { description, mimeType, width, height, url } = image

          if (mimeType === "image/svg+xml" && url) {
            const svgProps = width && height ? { width, height } : {}
            return (
              <img
                src={url}
                alt={description || ""}
                style={{ maxWidth: "100%" }}
                {...svgProps}
                {...ContentfulLivePreview.getProps({ entryId, fieldId: "image" })}
              />
            )
          }

          if (image?.gatsbyImageData) {
            const gatsbyImage = getImage(image.gatsbyImageData)
            if (gatsbyImage) {
              return (
                <GatsbyImage
                  className={styles?.gatsbyImageWrapper}
                  image={gatsbyImage}
                  alt={image?.description}
                  {...ContentfulLivePreview.getProps({
                    entryId,
                    fieldId: "image"
                  })}
                />
              )
            }
          }
        }
      }

      return null
    },

    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const target = node?.data?.target || {}
      const { gatsbyImageData, description, mimeType, width, height, url } =
        target

      if (gatsbyImageData) {
        const gatsbyImage = getImage(gatsbyImageData)
        if (gatsbyImage) {
          return (
            <GatsbyImage
              className={styles?.gatsbyImageWrapper}
              image={gatsbyImage}
              alt={description}
            />
          )
        }
      }

      if (node?.data?.target?.__typename === "ContentfulImage") {
        const gatsbyImageData = node?.data?.target?.image?.gatsbyImageData
        const description = node?.data?.target?.image?.description
        const gatsbyImage = gatsbyImageData ? getImage(gatsbyImageData) : null

        if (gatsbyImage) {
          return (
            <GatsbyImage
              className={styles?.gatsbyImageWrapper}
              image={gatsbyImage}
              alt={description}
            />
          )
        }
      }

      if (mimeType === "image/svg+xml" && url) {
        const svgProps = width && height ? { width, height } : {}
        return (
          <img
            src={url}
            alt={description || ""}
            style={{ maxWidth: "100%" }}
            {...svgProps}
          />
        )
      }

      return null
    },
    
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return (
        <Typography variant="body-md" as="p">
          {children}
        </Typography>
      )
    }
  },
  references: []
}

/**
 * Enhanced rich text renderer that includes global link handling
 */
export const renderEnhancedRichText = (
  raw?: string,
  options: RenderRichTextOptions = {},
  debugLog = false
): React.ReactNode => {
  if (!raw) {
    return null
  }

  const finalOptions = { 
    ...enhancedContentfulRenderOptions, 
    ...options,
    renderNode: {
      ...enhancedContentfulRenderOptions.renderNode,
      ...(options.renderNode || {})
    }
  }

  if (debugLog) {
    console.log("🚀 ~ Enhanced Rich Text DEBUG LOG:", { raw, options, finalOptions })
  }

  return renderContentfulRichText(
    {
      raw: raw,
      references: options.references ? options.references : []
    },
    finalOptions
  )
}

/**
 * Enhanced custom renderer to remove P tags while maintaining link handling
 */
export const enhancedCustomRendererToRemovePTag: RenderRichTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
    [INLINES.HYPERLINK]: hyperlinkRenderer
  }
}
